import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchBuyerQualityOverview,
  fetchBuyerQualityBuyers,
  suppressBuyer,
  unsuppressBuyer,
} from '../../lib/adminApi';

const TABS = ['Overview', 'All Buyers'] as const;
type Tab = (typeof TABS)[number];

function fmtRate(v: number | null | undefined): string {
  if (v == null) return '\u2014';
  return `${(v * 100).toFixed(1)}%`;
}

function fmtIrAccuracy(v: number | null | undefined): string {
  if (v == null) return '\u2014';
  return `${v.toFixed(1)}x`;
}

function fmtDisqualTime(seconds: number | null | undefined): string {
  if (seconds == null) return '\u2014';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function qualityScoreBadge(score: number | null | undefined): { label: string; classes: string } {
  if (score == null) return { label: '\u2014', classes: 'bg-slate-700 text-slate-300' };
  if (score <= 0) return { label: 'Suppressed', classes: 'bg-red-900/50 text-red-400' };
  if (score <= 0.3) return { label: 'Poor', classes: 'bg-red-900/50 text-red-400' };
  if (score <= 0.6) return { label: 'Below Avg', classes: 'bg-orange-900/50 text-orange-400' };
  if (score <= 1.0) return { label: 'Neutral', classes: 'bg-slate-700 text-slate-300' };
  return { label: 'Excellent', classes: 'bg-emerald-900/50 text-emerald-400' };
}

interface BuyerRow {
  exchange: string;
  buyer_id: string;
  sessions_30d: number;
  conversion_rate_30d: number | null;
  quality_fail_rate_30d: number | null;
  ir_accuracy: number | null;
  avg_disqual_time_seconds: number | null;
  click_waste_rate: number | null;
  quality_score: number | null;
  status: string;
  suppression_reason: string | null;
  suppression_until: string | null;
}

interface ExchangeBreakdown {
  exchange: string;
  buyer_count: number;
  suppressed_count: number;
  avg_conversion_rate: number | null;
  avg_quality_fail_rate: number | null;
}

interface PerformerRow {
  exchange: string;
  buyer_id: string;
  conversion_rate_30d: number | null;
  sessions_30d: number;
  ir_accuracy?: number | null;
  quality_fail_rate_30d?: number | null;
  status?: string;
}

// ── Tab: Overview ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'buyer-quality', 'overview'],
    queryFn: fetchBuyerQualityOverview,
  });

  if (isLoading) return <p className="text-slate-400 text-sm">Loading overview...</p>;
  if (!data) return <p className="text-slate-400 text-sm">No data available.</p>;

  const totalBuyers: number = data.totalBuyers ?? 0;
  const suppressedBuyers: number = data.suppressedBuyers ?? 0;
  const activeBuyers = totalBuyers - suppressedBuyers;
  const avgConversionRate: number | null = data.avgConversionRate ?? null;
  const suppressedPct = totalBuyers > 0 ? ((suppressedBuyers / totalBuyers) * 100).toFixed(1) : '0.0';

  const exchangeBreakdown: ExchangeBreakdown[] = data.exchangeBreakdown ?? [];
  const topPerformers: PerformerRow[] = data.topPerformers ?? [];
  const worstPerformers: PerformerRow[] = data.worstPerformers ?? [];

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Total Buyers</p>
          <p className="text-2xl font-bold text-white mt-1 tabular-nums">{totalBuyers.toLocaleString()}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Suppressed</p>
          <p className="text-2xl font-bold text-white mt-1 tabular-nums">{suppressedBuyers.toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-0.5">{suppressedPct}% of total</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Active</p>
          <p className="text-2xl font-bold text-white mt-1 tabular-nums">{activeBuyers.toLocaleString()}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Avg Conversion Rate</p>
          <p className="text-2xl font-bold text-white mt-1 tabular-nums">{fmtRate(avgConversionRate)}</p>
        </div>
      </div>

      {/* Exchange breakdown */}
      <div className="bg-slate-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-bold text-white">Exchange Breakdown</h3>
        {exchangeBreakdown.length === 0 ? (
          <p className="text-xs text-slate-500">No exchange data yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                  <th className="text-left py-2 pr-4">Exchange</th>
                  <th className="text-left py-2 pr-4">Buyers</th>
                  <th className="text-left py-2 pr-4">Suppressed</th>
                  <th className="text-left py-2 pr-4">Avg Conversion</th>
                  <th className="text-left py-2">Avg Quality Fail Rate</th>
                </tr>
              </thead>
              <tbody>
                {(exchangeBreakdown ?? []).map((row) => (
                  <tr key={row.exchange} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-2.5 pr-4 text-white font-medium">{row.exchange}</td>
                    <td className="py-2.5 pr-4 text-white tabular-nums">{(row.buyer_count ?? 0).toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-white tabular-nums">{(row.suppressed_count ?? 0).toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-white tabular-nums">{fmtRate(row.avg_conversion_rate)}</td>
                    <td className="py-2.5 text-white tabular-nums">{fmtRate(row.avg_quality_fail_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top performers */}
        <div className="bg-slate-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-white">Top Performers</h3>
          <p className="text-xs text-slate-500">These buyers are performing well on your panel</p>
          {topPerformers.length === 0 ? (
            <p className="text-xs text-slate-500">No data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                    <th className="text-left py-2 pr-4">Exchange</th>
                    <th className="text-left py-2 pr-4">Buyer ID</th>
                    <th className="text-left py-2 pr-4">30d Conversion</th>
                    <th className="text-left py-2 pr-4">Sessions</th>
                    <th className="text-left py-2">IR Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformers.map((row) => (
                    <tr key={`${row.exchange}-${row.buyer_id}`} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-2.5 pr-4 text-white">{row.exchange}</td>
                      <td className="py-2.5 pr-4 text-white font-mono text-xs">{row.buyer_id}</td>
                      <td className="py-2.5 pr-4 text-emerald-400 tabular-nums">{fmtRate(row.conversion_rate_30d)}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{(row.sessions_30d ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 text-white tabular-nums">{fmtIrAccuracy(row.ir_accuracy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Worst performers */}
        <div className="bg-slate-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-white">Worst Performers</h3>
          <p className="text-xs text-slate-500">These buyers may be hurting your supplier score</p>
          {worstPerformers.length === 0 ? (
            <p className="text-xs text-slate-500">No data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                    <th className="text-left py-2 pr-4">Exchange</th>
                    <th className="text-left py-2 pr-4">Buyer ID</th>
                    <th className="text-left py-2 pr-4">30d Conversion</th>
                    <th className="text-left py-2 pr-4">Sessions</th>
                    <th className="text-left py-2 pr-4">Quality Fail Rate</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {worstPerformers.map((row) => (
                    <tr key={`${row.exchange}-${row.buyer_id}`} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-2.5 pr-4 text-white">{row.exchange}</td>
                      <td className="py-2.5 pr-4 text-white font-mono text-xs">{row.buyer_id}</td>
                      <td className="py-2.5 pr-4 text-red-400 tabular-nums">{fmtRate(row.conversion_rate_30d)}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{(row.sessions_30d ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 pr-4 text-orange-400 tabular-nums">{fmtRate(row.quality_fail_rate_30d)}</td>
                      <td className="py-2.5">
                        <StatusBadge status={row.status ?? 'active'} suppressionUntil={null} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Status Badge ─────────────────────────────────────────────────────────

function StatusBadge({ status, suppressionUntil }: { status: string; suppressionUntil: string | null }) {
  if (status === 'suppressed' && suppressionUntil) {
    const until = new Date(suppressionUntil);
    const isPermanent = until.getFullYear() > 2099;
    if (isPermanent) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-400">
          Suppressed (permanent)
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/50 text-amber-400">
        Suppressed until {until.toLocaleDateString()}
      </span>
    );
  }
  if (status === 'suppressed') {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-400">
        Suppressed
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-900/50 text-emerald-400">
      Active
    </span>
  );
}

// ── Tab: All Buyers ──────────────────────────────────────────────────────

function AllBuyersTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [exchangeFilter, setExchangeFilter] = useState<string | undefined>(undefined);
  const [suppressedFilter, setSuppressedFilter] = useState<string | undefined>(undefined);
  const [suppressingBuyer, setSuppressingBuyer] = useState<string | null>(null);
  const [suppressHours, setSuppressHours] = useState(72);
  const [suppressReason, setSuppressReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'buyer-quality', 'buyers', page, exchangeFilter, suppressedFilter],
    queryFn: () => fetchBuyerQualityBuyers(page, 25, exchangeFilter, suppressedFilter),
  });

  const suppressMutation = useMutation({
    mutationFn: ({ exchange, buyerId, hours, reason }: { exchange: string; buyerId: string; hours: number; reason: string }) =>
      suppressBuyer(exchange, buyerId, hours, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'buyer-quality'] });
      setSuppressingBuyer(null);
      setSuppressHours(72);
      setSuppressReason('');
    },
  });

  const unsuppressMutation = useMutation({
    mutationFn: ({ exchange, buyerId }: { exchange: string; buyerId: string }) =>
      unsuppressBuyer(exchange, buyerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'buyer-quality'] });
    },
  });

  const exchangeOptions = [
    { value: undefined, label: 'All' },
    { value: 'purespectrum', label: 'PureSpectrum' },
    { value: 'rfg', label: 'RFG' },
  ] as const;

  const statusOptions = [
    { value: undefined, label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'suppressed', label: 'Suppressed' },
  ] as const;

  const buyers: BuyerRow[] = data?.buyers ?? [];

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-4">
        <div className="flex gap-1">
          {exchangeOptions.map((opt) => (
            <button
              key={opt.label}
              onClick={() => { setExchangeFilter(opt.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                exchangeFilter === opt.value
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {statusOptions.map((opt) => (
            <button
              key={opt.label}
              onClick={() => { setSuppressedFilter(opt.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                suppressedFilter === opt.value
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-slate-400 text-sm">Loading buyers...</p>
      ) : buyers.length === 0 ? (
        <p className="text-slate-400 text-sm">No buyers found.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                  <th className="text-left py-2 pr-4">Exchange</th>
                  <th className="text-left py-2 pr-4">Buyer ID</th>
                  <th className="text-left py-2 pr-4">Sessions (30d)</th>
                  <th className="text-left py-2 pr-4">Conversion</th>
                  <th className="text-left py-2 pr-4">Quality Fail</th>
                  <th className="text-left py-2 pr-4">IR Accuracy</th>
                  <th className="text-left py-2 pr-4">Avg Disqual</th>
                  <th className="text-left py-2 pr-4">Click Waste</th>
                  <th className="text-left py-2 pr-4">Quality Score</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-left py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((b) => {
                  const key = `${b.exchange}-${b.buyer_id}`;
                  const badge = qualityScoreBadge(b.quality_score);
                  const isSuppressing = suppressingBuyer === key;

                  return (
                    <tr key={key} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-2.5 pr-4 text-white">{b.exchange}</td>
                      <td className="py-2.5 pr-4 text-white font-mono text-xs">{b.buyer_id}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{(b.sessions_30d ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{fmtRate(b.conversion_rate_30d)}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{fmtRate(b.quality_fail_rate_30d)}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{fmtIrAccuracy(b.ir_accuracy)}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{fmtDisqualTime(b.avg_disqual_time_seconds)}</td>
                      <td className="py-2.5 pr-4 text-white tabular-nums">{fmtRate(b.click_waste_rate)}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4">
                        <StatusBadge status={b.status} suppressionUntil={b.suppression_until} />
                      </td>
                      <td className="py-2.5">
                        {b.status === 'suppressed' ? (
                          <button
                            onClick={() => unsuppressMutation.mutate({ exchange: b.exchange, buyerId: b.buyer_id })}
                            disabled={unsuppressMutation.isPending}
                            className="px-2 py-1 rounded text-xs font-medium bg-emerald-900/50 text-emerald-400 hover:bg-emerald-900/70 disabled:opacity-40"
                          >
                            Unsuppress
                          </button>
                        ) : isSuppressing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={suppressHours}
                              onChange={(e) => setSuppressHours(Number(e.target.value))}
                              className="w-16 px-2 py-1 rounded text-xs bg-slate-700 text-white border border-slate-600"
                              placeholder="Hours"
                              min={1}
                            />
                            <input
                              type="text"
                              value={suppressReason}
                              onChange={(e) => setSuppressReason(e.target.value)}
                              className="w-32 px-2 py-1 rounded text-xs bg-slate-700 text-white border border-slate-600"
                              placeholder="Reason"
                            />
                            <button
                              onClick={() => suppressMutation.mutate({ exchange: b.exchange, buyerId: b.buyer_id, hours: suppressHours, reason: suppressReason })}
                              disabled={suppressMutation.isPending || !suppressReason.trim()}
                              className="px-2 py-1 rounded text-xs font-medium bg-red-900/50 text-red-400 hover:bg-red-900/70 disabled:opacity-40"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setSuppressingBuyer(null)}
                              className="px-2 py-1 rounded text-xs font-medium text-slate-400 hover:text-white"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setSuppressingBuyer(key); setSuppressHours(72); setSuppressReason(''); }}
                            className="px-2 py-1 rounded text-xs font-medium bg-amber-900/50 text-amber-400 hover:bg-amber-900/70"
                          >
                            Suppress
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Page {data.page ?? page} of {data.totalPages ?? 1}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={page >= (data.totalPages ?? 1)}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function BuyerQualityPage() {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Buyer Quality</h1>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTab === tab
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-slate-900 rounded-xl p-5">
        {activeTab === 'Overview' && <OverviewTab />}
        {activeTab === 'All Buyers' && <AllBuyersTab />}
      </div>
    </div>
  );
}
